import Footer from '@/components/Footer';
import { ChangeExchangePriceFooterContent } from '@/components/manage/change-trips/ChangeExchangePriceFooterContent';
import TransferTime from '@/components/manage/tickets/TransferTime';
import CurrentTicketSection from '@/components/purchase/journey/CurrentTicketSection';
import InventoryClassTabs from '@/components/purchase/journey/InventoryClassTabs';
import JourneyOfferListSkeleton from '@/components/purchase/journey/JourneyOfferListSkeleton';
import JourneyOfferWarnings from '@/components/purchase/journey/JourneyOfferWarnings';
import OfferCard from '@/components/purchase/journey/OfferCard';
import ServiceTextMessage from '@/components/purchase/journey/ServiceTextMessage';
import StopDetails from '@/components/purchase/journey/StopDetails';
import TravelClassTabs from '@/components/purchase/journey/TravelClassTabs';
import { purchaseFlowBookingCreationLoadingSelector } from '@/features/loading/loadingSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransText } from '@/i18n/trans/text';
import { type RootState } from '@/store';
import { useSelector } from '@/store/utils';
import type {
  LegDTO,
  SearchJourneyOfferDTO,
  SearchJourneyOffersDTO,
} from '@/types/dto/agent';
import type {
  OfferMapByLegId,
  OfferMapItem,
  OffersWarning,
} from '@/types/offer';
import { breakpoints } from '@/utils/breakpoints';
import { getDurationBetweenTimestamps } from '@/utils/dateTime';
import {
  getJourneyLegIds,
  getJourneyNonWalkingLegIds,
  getLegClassView,
  type InventoryClass,
  type LegClassView,
  shouldDisplayOfferOnLeg,
  type TravelClass,
} from '@/utils/purchase';
import { isNonEmptyString } from '@/utils/typeGuards';
import {
  Button,
  Card,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import type { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';
import {
  type FC,
  Fragment,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';

interface JourneyOfferListProps {
  selectedJourney?: SearchJourneyOffersDTO;
  isOutbound: boolean;
  updateSelectedOfferMap: ActionCreatorWithPayload<OfferMapItem>;
  selectedOfferMapByLegIdSelector: Selector<RootState, OfferMapByLegId>;
  title: ReactNode;
  onContinue: () => void;
  onGoBack: () => void;
  isLoading?: boolean;
  warnings?: OffersWarning[];
}

const JourneyOfferList: FC<JourneyOfferListProps> = ({
  selectedJourney,
  isOutbound,
  updateSelectedOfferMap,
  selectedOfferMapByLegIdSelector,
  title,
  onContinue,
  onGoBack,
  isLoading = false,
  warnings,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const isPending = useSelector(purchaseFlowBookingCreationLoadingSelector);
  const {
    changeFlow,
    hasSelection,
    selectedInboundTrip,
    selectedOutboundTrip,
  } = useChangeBookingSelection();
  const selectedOfferMapByLegId = useSelector(selectedOfferMapByLegIdSelector);
  const isOutboundJourney = selectedJourney?.isOutbound ?? isOutbound;
  const selectedChangeTravelClassTrip =
    changeFlow === 'changeTravelClass'
      ? isOutboundJourney
        ? selectedOutboundTrip
        : selectedInboundTrip
      : null;
  const actionButtonLabelKey =
    changeFlow === 'changeTravelClass' &&
    (!isOutboundJourney || !selectedInboundTrip?.id)
      ? 'confirm'
      : 'continue';
  const showOffersAdvancedLayout =
    useTenantConfiguration()?.showOffersAdvancedLayout;

  // each leg keeps its own class selection, since availability may differ per leg
  const [inventoryClassByLegId, setInventoryClassByLegId] = useState<
    Record<string, InventoryClass>
  >({});
  const [travelClassByLegId, setTravelClassByLegId] = useState<
    Record<string, TravelClass>
  >({});

  const handleInventoryClassChange = useCallback(
    (legId: string, inventoryClass: InventoryClass) =>
      setInventoryClassByLegId((current) => ({
        ...current,
        [legId]: inventoryClass,
      })),
    []
  );

  const handleTravelClassChange = useCallback(
    (legId: string, travelClass: TravelClass) =>
      setTravelClassByLegId((current) => ({
        ...current,
        [legId]: travelClass,
      })),
    []
  );

  const legIds = useMemo(
    () => (selectedJourney ? getJourneyLegIds(selectedJourney) : []),
    [selectedJourney]
  );

  const offerableLegIds = useMemo(
    () => (selectedJourney ? getJourneyNonWalkingLegIds(selectedJourney) : []),
    [selectedJourney]
  );

  const getTripLegPart = useCallback(
    (legId?: string) => {
      if (!legId || !legIds.length) return;

      return `${legIds.indexOf(legId) + 1}/${legIds.length}`;
    },
    [legIds]
  );

  const getOffersOrderedByPriceByLeg = useCallback(
    (offers: Array<SearchJourneyOfferDTO> | undefined, leg: LegDTO) => {
      const legId = leg.id;
      if (!offers?.length || !legId) {
        return [];
      }

      return offers
        .filter(
          (offer) =>
            offer.coveredLegIds?.includes(legId) &&
            shouldDisplayOfferOnLeg(offer, legId, legIds)
        )
        .sort((a, b) => (a.price?.amount ?? 0) - (b.price?.amount ?? 0));
    },
    [legIds]
  );

  const getOffersColumns = useCallback(
    (offers: Array<SearchJourneyOfferDTO>) => {
      const firstColumn: Array<SearchJourneyOfferDTO> = [];
      const secondColumn: Array<SearchJourneyOfferDTO> = [];

      offers.forEach((offer, index) => {
        if (index % 2 === 0) {
          firstColumn.push(offer);
        } else {
          secondColumn.push(offer);
        }
      });

      return [firstColumn, secondColumn];
    },
    []
  );

  const renderOfferCards = useCallback(
    (offers: Array<SearchJourneyOfferDTO>, leg: LegDTO): ReactNode => {
      if (!offers?.length) {
        return null;
      }

      return offers.map((offer, i) => (
        <OfferCard
          key={`${offer.id}-${i}`}
          offer={offer}
          legId={leg.id}
          legIds={legIds}
          journeyDetails={{
            originStopName: leg.originStop?.name,
            destinationStopName: leg.destinationStop?.name,
          }}
          updateSelectedOfferMap={updateSelectedOfferMap}
          selectedOfferMapByLegIdSelector={selectedOfferMapByLegIdSelector}
        />
      ));
    },
    [legIds, selectedOfferMapByLegIdSelector, updateSelectedOfferMap]
  );

  const isOfferSelectionComplete = useMemo(() => {
    if (!selectedJourney || !offerableLegIds.length) {
      return false;
    }
    return offerableLegIds.every((legId) =>
      isNonEmptyString(selectedOfferMapByLegId[legId]?.id)
    );
  }, [offerableLegIds, selectedJourney, selectedOfferMapByLegId]);

  const isContinuePending = isPending || isLoading;
  const isContinueDisabled = isContinuePending || !isOfferSelectionComplete;
  const hasWarnings = Boolean(warnings?.length);

  const handleContinue = useCallback(() => {
    if (isContinueDisabled) {
      return;
    }
    onContinue();
  }, [isContinueDisabled, onContinue]);

  return (
    <>
      <div className="mx-auto flex w-full max-w-laptop flex-col p-0 pb-24 laptop:p-4 laptop:pb-24">
        <Typography
          variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
          className="p-4 laptop:p-0 laptop:pb-4"
          as="h1"
        >
          {title}
        </Typography>
        <Card
          className="rounded-none p-0 laptop:rounded-lg"
          data-testid="journey-offer-list"
        >
          {isLoading && !selectedJourney && <JourneyOfferListSkeleton />}
          {hasWarnings && (
            <div className="p-4">
              <JourneyOfferWarnings warnings={warnings} />
            </div>
          )}
          {selectedJourney?.trips?.map(({ offers, legs }) =>
            legs?.map((leg, key) => {
              const legIndex = leg.id ? legIds.indexOf(leg.id) : key;
              const nextLeg = legs[key + 1];
              const transferTime = nextLeg
                ? getDurationBetweenTimestamps(
                    leg.arrivalTime,
                    nextLeg.departureTime
                  )
                : null;
              const serviceTexts = (leg.serviceAttributes ?? []).filter(
                ({ code }) => code === 'SERVICE_TEXT'
              );

              const legId = leg.id;
              const orderedOffers = getOffersOrderedByPriceByLeg(offers, leg);
              const classView: LegClassView =
                legId && showOffersAdvancedLayout
                  ? getLegClassView(orderedOffers, {
                      inventoryClass: inventoryClassByLegId[legId],
                      travelClass: travelClassByLegId[legId],
                      preselectedOffer: selectedOfferMapByLegId[legId],
                    })
                  : {
                      inventoryClasses: [],
                      travelClasses: [],
                      visibleOffers: orderedOffers,
                    };
              const [firstColumn, secondColumn] = getOffersColumns(
                classView.visibleOffers
              );

              return (
                <Fragment key={key}>
                  <div className="flex flex-col gap-6 p-4 laptop:flex-row laptop:items-start">
                    <div className="flex flex-col gap-4 laptop:w-[328px]">
                      <div className="laptop:flex-shrink-0">
                        {legIds.length > 1 && (
                          <Typography
                            variant="body1-bold"
                            className="mb-1 pl-1.5 text-primary"
                            as="h2"
                          >
                            <TransText
                              i18nKey="tripLegPartIndex"
                              values={{
                                tripLegPart: getTripLegPart(leg.id),
                              }}
                            />
                          </Typography>
                        )}
                        <StopDetails leg={leg} />
                      </div>
                      <ServiceTextMessage serviceTexts={serviceTexts} />
                    </div>
                    <div className="flex flex-col gap-4 laptop:flex-1">
                      {legId && showOffersAdvancedLayout && (
                        <>
                          <InventoryClassTabs
                            tabs={classView.inventoryClasses}
                            legId={legId}
                            value={classView.inventoryClass}
                            onChange={handleInventoryClassChange}
                          />
                          <TravelClassTabs
                            tabs={classView.travelClasses}
                            legId={legId}
                            value={classView.travelClass}
                            onChange={handleTravelClassChange}
                          />
                        </>
                      )}
                      <CurrentTicketSection
                        trip={selectedChangeTravelClassTrip}
                        legIndex={legIndex >= 0 ? legIndex : key}
                      />
                      {selectedChangeTravelClassTrip && (
                        <Typography variant="body1-bold">
                          <TransText i18nKey="selectTicket" />
                        </Typography>
                      )}
                      {isLaptopOrBigger ? (
                        <div className="flex flex-row gap-3">
                          <div className="flex w-full flex-col gap-3">
                            {renderOfferCards(firstColumn, leg)}
                          </div>
                          <div className="flex w-full flex-col gap-3">
                            {renderOfferCards(secondColumn, leg)}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-4">
                          {renderOfferCards(classView.visibleOffers, leg)}
                        </div>
                      )}
                    </div>
                  </div>
                  {Boolean(transferTime) && (
                    <TransferTime formattedTime={transferTime} />
                  )}
                </Fragment>
              );
            })
          )}
        </Card>
      </div>
      <Footer
        content={
          hasSelection ? <ChangeExchangePriceFooterContent /> : undefined
        }
        actionButtons={
          <>
            <Button
              size={isLaptopOrBigger ? 'medium' : 'large'}
              data-testid="footer-back-button"
              className="rounded-lg laptop:w-auto"
              variant="tertiary"
              onClick={onGoBack}
              startAdornment={<Icons.arrowLeft />}
            >
              <TransText i18nKey="back" />
            </Button>
            <Button
              size={isLaptopOrBigger ? 'medium' : 'large'}
              data-testid="footer-continue-button"
              className="rounded-lg laptop:w-auto"
              variant="cta"
              onClick={handleContinue}
              isPending={isContinuePending}
              isDisabled={isContinueDisabled}
              endAdornment={<Icons.arrowRight />}
            >
              <TransText i18nKey={actionButtonLabelKey} />
            </Button>
          </>
        }
      />
    </>
  );
};

export default JourneyOfferList;
